(function () {
  "use strict";

  if (window.__cuffCardEnhancementsInitialized) return;
  window.__cuffCardEnhancementsInitialized = true;

  const reducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  );
  const responsiveWidths = [180, 260, 360, 480, 600, 800];
  let lazyVideoObserver;

  function elementsWithin(root, selector) {
    const elements = [];

    if (root instanceof Element && root.matches(selector)) {
      elements.push(root);
    }

    if (root && typeof root.querySelectorAll === "function") {
      elements.push(...root.querySelectorAll(selector));
    }

    return elements;
  }

  function urlWithWidth(url, width) {
    if (!url || !width) return url || "";
    if (/([?&])width=\d+/.test(url)) {
      return url.replace(/([?&])width=\d+/, `$1width=${width}`);
    }
    return `${url}${url.includes("?") ? "&" : "?"}width=${width}`;
  }

  function normalizeImage(image) {
    if (typeof image === "string") {
      return {
        url: image,
        alt: "",
        width: 800,
        height: 800,
      };
    }

    return {
      url: image?.url || "",
      alt: image?.alt || "",
      width: Number(image?.width) || 800,
      height: Number(image?.height) || Number(image?.width) || 800,
    };
  }

  function parseImages(swatch) {
    try {
      const parsed = JSON.parse(swatch.dataset.images || "{}");
      return (Array.isArray(parsed.images) ? parsed.images : [])
        .map(normalizeImage)
        .filter((image) => image.url);
    } catch (_error) {
      return [];
    }
  }

  function imageWidths(image) {
    const maximum = Math.max(1, Math.min(image.width || 800, 800));
    const widths = responsiveWidths.filter((width) => width <= maximum);

    if (!widths.includes(maximum)) {
      widths.push(maximum);
    }

    return [...new Set(widths)].sort((left, right) => left - right);
  }

  function createResponsiveImage(image) {
    const element = document.createElement("img");
    const widths = imageWidths(image);
    const sourceWidth = widths[widths.length - 1];

    element.loading = "lazy";
    element.decoding = "async";
    element.src = urlWithWidth(image.url, sourceWidth);
    element.srcset = widths
      .map((width) => `${urlWithWidth(image.url, width)} ${width}w`)
      .join(", ");
    element.sizes =
      "(min-width: 1200px) 25vw, (min-width: 768px) 33vw, 50vw";
    element.alt = image.alt;
    element.width = image.width;
    element.height = image.height;

    return element;
  }

  function createImageLink(image, href, productTitle) {
    const link = document.createElement("a");
    const accessibleName = image.alt || productTitle;

    link.className = "card-link";
    link.href = href;
    if (productTitle) link.title = productTitle;
    if (accessibleName) link.setAttribute("aria-label", accessibleName);
    link.append(createResponsiveImage(image));

    return link;
  }

  function createSlide(image, href, index, productTitle) {
    const slide = document.createElement("div");

    slide.className = `${
      index === 0
        ? "first-media-product"
        : index === 1
          ? "second-media-product"
          : "other-media-product"
    } media-product-card swiper-slide`;
    slide.append(createImageLink(image, href, productTitle));

    return slide;
  }

  function updateActiveSwatch(card, selectedSwatch) {
    card
      .querySelectorAll(".colors-selector-card .swatch-color-item")
      .forEach((swatch) => {
        const isSelected = swatch === selectedSwatch;
        const link = swatch.querySelector("a");

        swatch.classList.toggle("active-swatch", isSelected);
        if (!link) return;

        if (isSelected) {
          link.setAttribute("aria-current", "true");
        } else {
          link.removeAttribute("aria-current");
        }
      });
  }

  function updateHoverMedia(card, image, href, productTitle) {
    const firstHoverMedia = card.querySelector(
      ".media-for-hover .first-media-product"
    );

    if (!firstHoverMedia || !image) return;
    firstHoverMedia.replaceChildren(
      createImageLink(image, href, productTitle)
    );
  }

  function showFirstSlide(swiper) {
    if (!swiper || swiper.destroyed) return;

    if (swiper.params.loop && typeof swiper.slideToLoop === "function") {
      swiper.slideToLoop(0, 0, false);
    } else {
      swiper.slideTo(0, 0, false);
    }
  }

  function updateCardSlider(card, images, href, productTitle) {
    const swiperElement = card.querySelector(
      ".card-media--wrapper.swiper"
    );
    const wrapper = swiperElement?.querySelector(".swiper-wrapper");

    updateHoverMedia(card, images[0], href, productTitle);
    if (!swiperElement || !wrapper) return;
    if (swiperElement.dataset.swatchUpdating === "true") return;

    swiperElement.dataset.swatchUpdating = "true";

    try {
      const slides = images
        .slice(0, swiperElement.hasAttribute('data-cuff-card-gallery') ? images.length : 5)
        .map((image, index) =>
          createSlide(image, href, index, productTitle)
        );
      const swiper = swiperElement.swiper;

      if (
        swiper &&
        !swiper.destroyed &&
        typeof swiper.removeAllSlides === "function" &&
        typeof swiper.appendSlide === "function"
      ) {
        swiper.removeAllSlides();
        swiper.appendSlide(slides);
        swiper.update();
        showFirstSlide(swiper);
      } else {
        wrapper.replaceChildren(...slides);

        if (
          typeof window.initCardSlider === "function" &&
          !swiperElement.swiper
        ) {
          window.initCardSlider([swiperElement]);
          showFirstSlide(swiperElement.swiper);
        }
      }

      swiperElement.dispatchEvent(
        new CustomEvent("card:media-updated", {
          bubbles: true,
          detail: { images },
        })
      );
    } finally {
      delete swiperElement.dataset.swatchUpdating;
    }
  }

  function applySwatchBackgrounds(root) {
    elementsWithin(root, ".colors-selector-card .swatch-color-item").forEach(
      (swatch) => {
        if (swatch.style.backgroundImage || swatch.style.backgroundColor) {
          return;
        }

        const url = swatch.dataset.featured || swatch.dataset.image;
        if (url) swatch.style.backgroundImage = `url("${url}")`;
      }
    );
  }

  function loadVideo(video) {
    let sourceChanged = false;

    video.querySelectorAll("source[data-src]").forEach((source) => {
      if (!source.dataset.src) return;
      source.src = source.dataset.src;
      source.removeAttribute("data-src");
      sourceChanged = true;
    });

    if (sourceChanged) video.load();
    video.classList.remove("lazy");

    const slideshow = video.closest("[data-auto-video]");
    const isSingleAutoplaySlideshow =
      slideshow?.dataset.autoVideo === "true" &&
      !slideshow.hasAttribute("data-init-slideshow");

    if (
      !reducedMotion.matches &&
      (video.dataset.cardVideoAutoplay === "true" ||
        isSingleAutoplaySlideshow)
    ) {
      video.play().catch(() => {});
    }
  }

  function syncVideoMotion(video) {
    const slideshow = video.closest("[data-auto-video]");
    const preserveSingleSlideshowAutoplay =
      !reducedMotion.matches &&
      slideshow?.dataset.autoVideo === "true" &&
      !slideshow.hasAttribute("data-init-slideshow");

    if (!preserveSingleSlideshowAutoplay) {
      video.autoplay = false;
      video.removeAttribute("autoplay");
    }

    if (reducedMotion.matches) {
      video.pause();
    }
  }

  function observeVideos(root) {
    const videos = elementsWithin(root, "video.lazy, video[data-card-video]");

    videos.forEach(syncVideoMotion);

    if (!("IntersectionObserver" in window)) {
      videos.forEach(loadVideo);
      return;
    }

    if (!lazyVideoObserver) {
      lazyVideoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            loadVideo(entry.target);
            lazyVideoObserver.unobserve(entry.target);
            delete entry.target.dataset.cardVideoObserved;
          });
        },
        { rootMargin: "200px 0px" }
      );
    }

    videos.forEach((video) => {
      if (video.dataset.cardVideoObserved === "true") return;
      video.dataset.cardVideoObserved = "true";
      lazyVideoObserver.observe(video);
    });
  }

  function initializeCards(root) {
    applySwatchBackgrounds(root);
    observeVideos(root);
  }

  document.addEventListener("click", (event) => {
    const swatch = event.target.closest(
      ".card .colors-selector-card .swatch-color-item"
    );

    if (!swatch) return;

    const card = swatch.closest(".card");
    const images = parseImages(swatch);

    if (!card || !images.length) return;

    event.preventDefault();
    event.stopPropagation();

    const href =
      swatch.querySelector("a")?.getAttribute("href") ||
      card.querySelector(".card-link")?.getAttribute("href") ||
      "#";
    const productTitle =
      swatch.dataset.productTitle ||
      card.querySelector(".card-title")?.textContent.trim() ||
      images[0].alt;

    updateActiveSwatch(card, swatch);
    updateCardSlider(card, images, href, productTitle);
  });

  function updateCustomizationDecision(source) {
    const button = document.querySelector(
      "#product-add-to-cart[data-customization-filled]"
    );

    if (!button || !source) return;

    const propertyHasValue = (field) => {
      if (!field || field.disabled) return false;

      if (field.type === "checkbox" || field.type === "radio") {
        return field.checked && String(field.value || "").trim().length > 0;
      }

      return String(field.value || "").trim().length > 0;
    };

    const personalizationRoot =
      source.closest("[data-pf-wrap]") ||
      source.closest(".pplr-wrapper") ||
      button.closest(".productView") ||
      document;
    const fields = Array.from(
      personalizationRoot.querySelectorAll(
        '[name^="properties["]:not([type="hidden"])'
      )
    );

    button.dataset.customizationFilled = fields.some(propertyHasValue)
      ? "true"
      : "false";
  }

  document.addEventListener("input", (event) => {
    if (
      !event.target.matches(
        '.pf-input, .pf-select, .pplr-wrapper [name^="properties["]'
      )
    ) {
      return;
    }

    updateCustomizationDecision(event.target);
  });

  document.addEventListener("change", (event) => {
    if (
      !event.target.matches(
        '.pf-input, .pf-select, .pplr-wrapper [name^="properties["]'
      )
    ) {
      return;
    }

    updateCustomizationDecision(event.target);
  });

  document.addEventListener("click", (event) => {
    const personalizerButton = event.target.closest(
      ".pplr-btn.p_a_t_c, .pplr-btn.ptc_button"
    );

    if (!personalizerButton || personalizerButton.id === "product-add-to-cart") {
      return;
    }

    updateCustomizationDecision(personalizerButton);
  });

  document.addEventListener(
    "play",
    (event) => {
      if (
        reducedMotion.matches &&
        event.target.matches?.("video[data-card-video]")
      ) {
        event.target.pause();
      }
    },
    true
  );

  document.addEventListener("products:rendered", (event) => {
    initializeCards(event.target);
  });

  document.addEventListener("shopify:section:load", (event) => {
    initializeCards(event.target);
  });

  const handleMotionPreferenceChange = () => {
    document
      .querySelectorAll("video[data-card-video]")
      .forEach(syncVideoMotion);
  };

  if (typeof reducedMotion.addEventListener === "function") {
    reducedMotion.addEventListener("change", handleMotionPreferenceChange);
  } else if (typeof reducedMotion.addListener === "function") {
    reducedMotion.addListener(handleMotionPreferenceChange);
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => initializeCards(document),
      { once: true }
    );
  } else {
    initializeCards(document);
  }
})();
